import Konva from "konva";
import { resolveFxGpuModuleId } from "./fxEffectCatalog";
import { resolveFxBlendComposite, resolveFxMaskClipPath, type FxRenderPacket } from "./fxRuntime";
import { FxGpuRenderer, type FxGpuSource } from "./fxGpuRenderer";
import { getFxGpuModule } from "./fxGpuModules";

export type FxGpuOverlayEntry = {
  sourceNode: Konva.Node;
  overlayNode: Konva.Image;
  canvas: HTMLCanvasElement;
  compositeCanvas: HTMLCanvasElement;
  renderer: FxGpuRenderer;
  lastRenderMs: number;
  lastAnamorphicLogMs: number;
  moduleId: string | null;
  size: { width: number; height: number };
};

export type FxGpuOverlayMap = Map<string, FxGpuOverlayEntry>;

export type FxGpuOverlaySyncResult = {
  active: boolean;
  replaceBase: boolean;
  /** True when the render was skipped and the previous frame was reused (throttle hit). */
  throttled?: boolean;
};

const GPU_MODULE_RETRY_DELAY_MS = 45_000;
const gpuModuleFailureUntilMs = new Map<string, number>();
const gpuModuleFailureLogged = new Set<string>();

// ---------------------------------------------------------------------------
// Module-level scratch canvases for CPU fallback effects.
// All CPU fallback rendering is synchronous on the main thread, so two
// persistent canvases cover all cases without allocation per frame:
//   _cpuCanvasA — highlight extraction (extractHighlightTintCanvas, applyFogHazeFallback)
//   _cpuCanvasB — glow compositing (buildBlurredGlowCanvas)
// Sizes are updated in-place each call; no allocation after the first frame.
// ---------------------------------------------------------------------------
let _cpuCanvasA: HTMLCanvasElement | null = null;
let _cpuCanvasB: HTMLCanvasElement | null = null;

function getCpuCanvasA(w: number, h: number): HTMLCanvasElement {
  if (!_cpuCanvasA) _cpuCanvasA = document.createElement("canvas");
  _cpuCanvasA.width = w;
  _cpuCanvasA.height = h;
  return _cpuCanvasA;
}

function getCpuCanvasB(w: number, h: number): HTMLCanvasElement {
  if (!_cpuCanvasB) _cpuCanvasB = document.createElement("canvas");
  _cpuCanvasB.width = w;
  _cpuCanvasB.height = h;
  return _cpuCanvasB;
}

export type FxCpuFallbackFamily =
  | "bloom-color"
  | "color-grade"
  | "chromatic-aberration"
  | "anamorphic"
  | "leak-flash"
  | "glitch"
  | "warp-blur"
  | "particle-noise"
  | "fog-haze"
  | "vignette";

export type FxCpuFallbackVariant =
  | "bloom-threshold"
  | "halation-glow"
  | "dream-glow"
  | "chrome-glow"
  | "soft-film-fade"
  | "bleach-bypass"
  | "kodak-lut-pack"
  | "legacy-filter-stack"
  | "chromatic-aberration-soft"
  | "anamorphic-streak"
  | "film-burn"
  | "light-leak"
  | "beat-light-leak"
  | "beat-bar-flash"
  | "impact-flash"
  | "data-mosh"
  | "glitch-tear"
  | "screen-crack"
  | "chromatic-glitch"
  | "beat-rgb-pulse"
  | "beat-scanline"
  | "glitch"
  | "speed-ramp-blur"
  | "heat-distortion"
  | "water-droplets"
  | "fog-depth"
  | "impact-shockwave"
  | "tempo-ripple"
  | "warp-blur"
  | "film-grain"
  | "lens-dust"
  | "film-scratches"
  | "beat-particle-burst"
  | "treble-sparkle"
  | "particle-noise"
  | "fog-haze"
  | "vignette";

export type FxCpuFallbackTuning = {
  intensityScale: number;
  amplitudeScale: number;
};
const FX_CPU_FALLBACK_TUNING: Partial<Record<FxCpuFallbackVariant, FxCpuFallbackTuning>> = {
  "anamorphic-streak": { intensityScale: 0.88, amplitudeScale: 0.9 },
  "bloom-threshold": { intensityScale: 0.82, amplitudeScale: 0.9 },
  "halation-glow": { intensityScale: 0.78, amplitudeScale: 0.88 },
  "dream-glow": { intensityScale: 0.84, amplitudeScale: 0.9 },
  "chrome-glow": { intensityScale: 0.82, amplitudeScale: 0.9 },
  "film-burn": { intensityScale: 0.86, amplitudeScale: 0.92 },
  "soft-film-fade": { intensityScale: 0.8, amplitudeScale: 0.86 },
  "film-grain": { intensityScale: 0.72, amplitudeScale: 1 },
  "lens-dust": { intensityScale: 0.74, amplitudeScale: 1 },
  "speed-ramp-blur": { intensityScale: 0.9, amplitudeScale: 0.95 },
  "beat-light-leak": { intensityScale: 0.84, amplitudeScale: 1 },
  "heat-distortion": { intensityScale: 0.9, amplitudeScale: 0.85 },
};

type FxCpuFallbackTuningOverride = Partial<Record<FxCpuFallbackVariant | "__default", Partial<FxCpuFallbackTuning>>>;
const FX_CPU_FALLBACK_TUNING_STORAGE_KEY = "vb_fx_cpu_fallback_tuning";
const FX_CPU_FALLBACK_TUNING_REFRESH_MS = 500;
let fxCpuFallbackTuningCache = {
  raw: "",
  parsed: {} as Partial<Record<FxCpuFallbackVariant, FxCpuFallbackTuning>>,
  defaultOverride: null as FxCpuFallbackTuning | null,
  lastReadMs: 0,
};

export function resolveFxCpuFallbackFamily(moduleId: string | null | undefined): FxCpuFallbackFamily | null {
  const module = String(moduleId || "").toLowerCase();
  if (!module) return null;
  if (module.includes("chromaticaberration")) return "chromatic-aberration";
  if (module.includes("bleach") || module.includes("kodak") || module.includes("legacyfilterstack")) return "color-grade";
  if (
    module.includes("halation") ||
    module.includes("bloom") ||
    module.includes("glow") ||
    module.includes("softfilmfade")
  ) {
    return "bloom-color";
  }
  if (module.includes("anamorphic")) return "anamorphic";
  if (
    module.includes("lightleak") ||
    module.includes("filmburn") ||
    module.includes("bokeh") ||
    module.includes("barflash") ||
    module.includes("impactflash") ||
    module.includes("flashwhite")
  ) {
    return "leak-flash";
  }
  if (
    module.includes("glitch") ||
    module.includes("tear") ||
    module.includes("datamosh") ||
    module.includes("chromatic") ||
    module.includes("scanline") ||
    module.includes("screencrack") ||
    module.includes("rgbpulse")
  ) {
    return "glitch";
  }
  if (
    module.includes("ripple") ||
    module.includes("shockwave") ||
    module.includes("barrel") ||
    module.includes("twirl") ||
    module.includes("heat") ||
    module.includes("water") ||
    module.includes("motionblur") ||
    module.includes("speedramp") ||
    module.includes("ghosttrail")
  ) {
    return "warp-blur";
  }
  if (
    module.includes("grain") ||
    module.includes("dust") ||
    module.includes("particle") ||
    module.includes("sparkle") ||
    module.includes("scratches") ||
    module.includes("embers") ||
    module.includes("confetti") ||
    module.includes("sparks") ||
    module.includes("snow") ||
    module.includes("rain")
  ) {
    return "particle-noise";
  }
  if (module.includes("haze") || module.includes("fog") || module.includes("smoke")) {
    return "fog-haze";
  }
  if (module.includes("vignette")) return "vignette";
  return null;
}

export function resolveFxCpuFallbackVariant(moduleId: string | null | undefined): FxCpuFallbackVariant | null {
  const module = String(moduleId || "").toLowerCase();
  if (!module) return null;
  if (module.includes("chromaticaberration")) return "chromatic-aberration-soft";
  if (module.includes("bleach")) return "bleach-bypass";
  if (module.includes("kodak")) return "kodak-lut-pack";
  if (module.includes("legacyfilterstack")) return "legacy-filter-stack";
  if (module.includes("anamorphic")) return "anamorphic-streak";
  if (module.includes("dreamglow")) return "dream-glow";
  if (module.includes("chromeglow")) return "chrome-glow";
  if (module.includes("halation")) return "halation-glow";
  if (module.includes("bloom")) return "bloom-threshold";
  if (module.includes("softfilmfade")) return "soft-film-fade";
  if (module.includes("filmburn")) return "film-burn";
  if (module.includes("beatlightleak")) return "beat-light-leak";
  if (module.includes("barflash")) return "beat-bar-flash";
  if (module.includes("impactflash") || module.includes("flashwhite")) return "impact-flash";
  if (module.includes("lightleak") || module.includes("bokeh")) {
    return "light-leak";
  }
  if (module.includes("beatrgbpulse")) return "beat-rgb-pulse";
  if (module.includes("beatscanline")) return "beat-scanline";
  if (module.includes("datamosh")) return "data-mosh";
  if (module.includes("glitchtear")) return "glitch-tear";
  if (module.includes("screencrack")) return "screen-crack";
  if (module.includes("chromatic") || module.includes("rgbpulse") || module.includes("scanline")) return "chromatic-glitch";
  if (
    module.includes("glitch") ||
    module.includes("tear") ||
    module.includes("datamosh")
  ) {
    return "glitch";
  }
  if (module.includes("speedramp")) return "speed-ramp-blur";
  if (module.includes("heat")) return "heat-distortion";
  if (module.includes("water")) return "water-droplets";
  if (module.includes("fogdepth")) return "fog-depth";
  if (module.includes("impactshockwave")) return "impact-shockwave";
  if (module.includes("temporipple")) return "tempo-ripple";
  if (
    module.includes("ripple") ||
    module.includes("shockwave") ||
    module.includes("barrel") ||
    module.includes("twirl") ||
    module.includes("motionblur") ||
    module.includes("speedramp") ||
    module.includes("ghosttrail")
  ) {
    return "warp-blur";
  }
  if (module.includes("filmgrain")) return "film-grain";
  if (module.includes("lensdust") || module.includes("dustoverlay") || module.includes("sparkledust")) return "lens-dust";
  if (module.includes("scratches")) return "film-scratches";
  if (module.includes("beatparticleburst")) return "beat-particle-burst";
  if (module.includes("treblesparkle")) return "treble-sparkle";
  if (
    module.includes("dust") ||
    module.includes("grain") ||
    module.includes("particle") ||
    module.includes("sparkle") ||
    module.includes("embers") ||
    module.includes("confetti") ||
    module.includes("sparks") ||
    module.includes("snow") ||
    module.includes("rain")
  ) {
    return "particle-noise";
  }
  if (module.includes("haze") || module.includes("fog") || module.includes("smoke")) return "fog-haze";
  if (module.includes("vignette")) return "vignette";
  return null;
}

export function resolveFxCpuFallbackTuning(variant: FxCpuFallbackVariant | null): FxCpuFallbackTuning {
  const base = variant ? FX_CPU_FALLBACK_TUNING[variant] ?? null : null;
  const overrideState = readFxCpuFallbackTuningOverrides();
  const defaultOverride = overrideState.defaultOverride;
  const variantOverride = variant ? overrideState.parsed[variant] ?? null : null;

  const intensityScale = clamp(
    toFinite(
      variantOverride?.intensityScale ??
        defaultOverride?.intensityScale ??
        base?.intensityScale ??
        1,
      1
    ),
    0,
    3
  );
  const amplitudeScale = clamp(
    toFinite(
      variantOverride?.amplitudeScale ??
        defaultOverride?.amplitudeScale ??
        base?.amplitudeScale ??
        1,
      1
    ),
    0,
    3
  );
  return {
    intensityScale,
    amplitudeScale,
  };
}

export function parseFxCpuFallbackTuningOverrides(
  raw: string | null | undefined
): { parsed: Partial<Record<FxCpuFallbackVariant, FxCpuFallbackTuning>>; defaultOverride: FxCpuFallbackTuning | null } {
  if (!raw || typeof raw !== "string") {
    return { parsed: {}, defaultOverride: null };
  }
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return { parsed: {}, defaultOverride: null };
  }
  if (!decoded || typeof decoded !== "object") {
    return { parsed: {}, defaultOverride: null };
  }
  const out: Partial<Record<FxCpuFallbackVariant, FxCpuFallbackTuning>> = {};
  let defaultOverride: FxCpuFallbackTuning | null = null;
  const source = decoded as FxCpuFallbackTuningOverride;
  for (const [key, value] of Object.entries(source)) {
    if (!value || typeof value !== "object") continue;
    const intensityScale = clamp(toFinite((value as any).intensityScale, 1), 0, 3);
    const amplitudeScale = clamp(toFinite((value as any).amplitudeScale, 1), 0, 3);
    const normalized = { intensityScale, amplitudeScale };
    if (key === "__default") {
      defaultOverride = normalized;
      continue;
    }
    out[key as FxCpuFallbackVariant] = normalized;
  }
  return { parsed: out, defaultOverride };
}

function readFxCpuFallbackTuningOverrides(): {
  parsed: Partial<Record<FxCpuFallbackVariant, FxCpuFallbackTuning>>;
  defaultOverride: FxCpuFallbackTuning | null;
} {
  const now = Date.now();
  if (now - fxCpuFallbackTuningCache.lastReadMs < FX_CPU_FALLBACK_TUNING_REFRESH_MS) {
    return {
      parsed: fxCpuFallbackTuningCache.parsed,
      defaultOverride: fxCpuFallbackTuningCache.defaultOverride,
    };
  }
  fxCpuFallbackTuningCache.lastReadMs = now;
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      fxCpuFallbackTuningCache.raw = "";
      fxCpuFallbackTuningCache.parsed = {};
      fxCpuFallbackTuningCache.defaultOverride = null;
      return { parsed: {}, defaultOverride: null };
    }
    const raw = window.localStorage.getItem(FX_CPU_FALLBACK_TUNING_STORAGE_KEY) ?? "";
    if (raw !== fxCpuFallbackTuningCache.raw) {
      fxCpuFallbackTuningCache.raw = raw;
      const parsed = parseFxCpuFallbackTuningOverrides(raw);
      fxCpuFallbackTuningCache.parsed = parsed.parsed;
      fxCpuFallbackTuningCache.defaultOverride = parsed.defaultOverride;
    }
    return {
      parsed: fxCpuFallbackTuningCache.parsed,
      defaultOverride: fxCpuFallbackTuningCache.defaultOverride,
    };
  } catch {
    fxCpuFallbackTuningCache.raw = "";
    fxCpuFallbackTuningCache.parsed = {};
    fxCpuFallbackTuningCache.defaultOverride = null;
    return { parsed: {}, defaultOverride: null };
  }
}

export function syncFxGpuOverlay(args: {
  elementId: string;
  node: Konva.Node;
  packets: FxRenderPacket[];
  overlayMap: FxGpuOverlayMap;
  nowMs?: number;
  /**
   * Minimum ms between re-renders for this element.
   * 0 or omitted = re-render every frame.
   * Use POSITIVE_INFINITY to freeze the current frame without removing the overlay.
   */
  throttleMs?: number;
}): FxGpuOverlaySyncResult {
  const { elementId, node, packets, overlayMap } = args;
  const nowMs = args.nowMs ?? performance.now();

  const gpuPackets = pickGpuPackets(packets);
  if (gpuPackets.length === 0) {
    removeFxGpuOverlay(elementId, overlayMap);
    return { active: false, replaceBase: false };
  }

  // Throttle: reuse last rendered frame if we're within the per-frame budget window.
  // The entry must exist and have been rendered at least once (lastRenderMs > 0).
  const throttleMs = args.throttleMs ?? 0;
  if (throttleMs > 0) {
    const existing = overlayMap.get(elementId);
    if (existing && existing.lastRenderMs > 0 && (nowMs - existing.lastRenderMs) < throttleMs) {
      return { active: true, replaceBase: false, throttled: true };
    }
  }

  const parent = node.getParent();
  if (!parent) {
    removeFxGpuOverlay(elementId, overlayMap);
    return { active: false, replaceBase: false };
  }

  const source = resolveNodeSource(node, elementId);
  if (!source) {
    removeFxGpuOverlay(elementId, overlayMap);
    return { active: false, replaceBase: false };
  }

  const size = resolveNodeSize(node, source);
  if (size.width <= 0 || size.height <= 0) {
    removeFxGpuOverlay(elementId, overlayMap);
    return { active: false, replaceBase: false };
  }

  const entry = ensureOverlayEntry({
    elementId,
    node,
    parent,
    overlayMap,
    moduleId: null,
    size,
  });

  let rendered = false;
  let replaceBase = false;
  let lastBlendMode = gpuPackets[gpuPackets.length - 1]?.blendMode ?? "normal";
  let lastOpacity = gpuPackets[gpuPackets.length - 1]?.opacity ?? 1;
  let lastModuleId: string | null = null;
  let chainSource: FxGpuSource = source;

  for (let passIndex = 0; passIndex < gpuPackets.length; passIndex++) {
    const gpuPacket = gpuPackets[passIndex];
    const isLastPass = passIndex === gpuPackets.length - 1;
    const moduleId = gpuPacket.gpuModuleId ?? resolveFxGpuModuleId(gpuPacket.effectType);
    const module = getFxGpuModule(moduleId);
    const fallbackFamily = resolveFxCpuFallbackFamily(moduleId);
    if (import.meta.env?.DEV && gpuPacket.category === "filter" && (!moduleId || (!module && !fallbackFamily))) {
      throw new Error(`Effect not implemented: ${gpuPacket.effectType}`);
    }

    const anamorphicDebug = resolveAnamorphicDebugState({
      packet: gpuPacket,
      moduleId,
    });
    const blockedUntil = module ? gpuModuleFailureUntilMs.get(module.id) ?? 0 : 0;
    let renderedByGpu = false;
    let passRendered = false;

    if (module && blockedUntil <= nowMs && !anamorphicDebug.forceCpuRoute) {
      const uniforms = module.resolveUniforms({
        packet: gpuPacket,
        nowMs,
        width: size.width,
        height: size.height,
      });

      passRendered = entry.renderer.render({
        moduleId: module.id,
        source: chainSource,
        uniforms,
      });

      if (passRendered) {
        renderedByGpu = true;
        if (gpuModuleFailureUntilMs.has(module.id)) {
          gpuModuleFailureUntilMs.delete(module.id);
          gpuModuleFailureLogged.delete(module.id);
        }
        replaceBase = replaceBase || module.kind === "replace";
      } else {
        markGpuModuleFailure(module.id, nowMs);
      }
    }

    if (!passRendered) {
      passRendered = renderCpuFallbackOverlay({
        entry,
        packet: gpuPacket,
        source: chainSource,
        moduleId,
        nowMs,
        anamorphicDebug,
      });
      replaceBase = replaceBase || (!!module && module.kind === "replace");
    }

    if (!passRendered) continue;

    // Only snapshot (GPU→CPU copy) when the result is needed as the source for the next pass.
    // On the last pass the compositeCanvas is never read — skipping it eliminates one
    // synchronous GPU→CPU readback per element per frame for the common single-effect case.
    if (!isLastPass) {
      const compositeSource = snapshotOverlayCanvas(entry);
      if (compositeSource) chainSource = compositeSource;
    }
    rendered = true;
    lastBlendMode = gpuPacket.blendMode;
    lastOpacity = gpuPacket.opacity ?? 1;
    lastModuleId = moduleId ?? lastModuleId;

    if (anamorphicDebug.log) {
      maybeLogAnamorphicRoute({
        entry,
        nowMs,
        route: renderedByGpu ? "GPU" : "CPU",
        packet: gpuPacket,
        moduleId,
        params: renderedByGpu
          ? computeAnamorphicGpuDiagnostics({
              intensity: clamp01(toFinite(gpuPacket.params?.intensity, 1)),
              amplitude: clamp(toFinite(gpuPacket.params?.amplitude, 1), 0, 3),
              validationMode: anamorphicDebug.validationMode,
            })
          : computeAnamorphicCpuDiagnostics({
              intensity: clamp01(toFinite(gpuPacket.params?.intensity, 1)),
              amplitude: clamp(toFinite(gpuPacket.params?.amplitude, 1), 0, 3),
              validationMode: anamorphicDebug.validationMode,
            }),
        maskDebug: anamorphicDebug.showHighlightMask,
        forcedValidation: anamorphicDebug.validationMode,
        chain: renderedByGpu
          ? "fxGpuOverlays.sync -> FxGpuRenderer.render -> overlay composite"
          : "fxGpuOverlays.sync -> renderCpuFallbackOverlay -> applyAnamorphicStreakFallback",
      });
    }
  }

  if (!rendered) {
    removeFxGpuOverlay(elementId, overlayMap);
    return { active: false, replaceBase: false };
  }

  entry.overlayNode.image(entry.canvas);
  syncOverlayTransform(entry.overlayNode, node);
  entry.overlayNode.opacity(gpuPackets.length > 1 ? 1 : lastOpacity);
  entry.overlayNode.globalCompositeOperation(resolveFxBlendComposite(lastBlendMode));
  entry.moduleId = lastModuleId ?? null;
  entry.lastRenderMs = nowMs;

  const maskClip = resolveFxMaskClipPath({
    packets,
    width: size.width,
    height: size.height,
  });
  const overlayAny = entry.overlayNode as any;
  if (typeof overlayAny.clipFunc === "function") {
    overlayAny.clipFunc(maskClip || null);
  }

  placeOverlayAbove({ parent, source: node, overlay: entry.overlayNode });

  if (replaceBase) {
    node.opacity(0);
  }

  return { active: true, replaceBase };
}

export function cleanupFxGpuOverlays(args: {
  overlayMap: FxGpuOverlayMap;
  keepElementIds: Set<string>;
}): boolean {
  const { overlayMap, keepElementIds } = args;
  let changed = false;
  for (const elementId of overlayMap.keys()) {
    if (keepElementIds.has(elementId)) continue;
    removeFxGpuOverlay(elementId, overlayMap);
    changed = true;
  }
  return changed;
}

export function disposeFxGpuOverlays(overlayMap: FxGpuOverlayMap): boolean {
  let changed = false;
  for (const elementId of overlayMap.keys()) {
    removeFxGpuOverlay(elementId, overlayMap);
    changed = true;
  }
  return changed;
}

function pickGpuPackets(packets: FxRenderPacket[]): FxRenderPacket[] {
  const out: FxRenderPacket[] = [];
  for (const packet of packets) {
    const moduleId = packet.gpuModuleId ?? resolveFxGpuModuleId(packet.effectType);
    if (!moduleId) continue;
    out.push(packet);
  }
  return out;
}

function resolveNodeSource(node: Konva.Node, elementId?: string): FxGpuSource | null {
  const nodeAny = node as any;
  const direct = resolveImageFromNode(nodeAny);
  if (direct) return direct;

  const child = resolveImageFromChildren(nodeAny, elementId);
  if (child) return child;

  if (typeof nodeAny.toCanvas === "function") {
    try {
      const canvas = nodeAny.toCanvas();
      if (canvas instanceof HTMLCanvasElement) return canvas;
    } catch {}
  }
  return null;
}

function resolveImageFromNode(nodeAny: any): FxGpuSource | null {
  if (!nodeAny) return null;
  const image = typeof nodeAny.image === "function" ? nodeAny.image() : null;
  const attrVideo = typeof nodeAny.getAttr === "function" ? nodeAny.getAttr("vbVideo") : null;
  const attrSource = typeof nodeAny.getAttr === "function" ? nodeAny.getAttr("vbSource") : null;
  return resolveCanvasSource(image) ?? resolveCanvasSource(attrVideo) ?? resolveCanvasSource(attrSource);
}

function resolveCanvasSource(value: unknown): FxGpuSource | null {
  if (!value) return null;
  if (value instanceof HTMLVideoElement || value instanceof HTMLImageElement || value instanceof HTMLCanvasElement) {
    return value;
  }
  if (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap) return value;
  if (typeof OffscreenCanvas !== "undefined" && value instanceof OffscreenCanvas) return value;
  return null;
}

function resolveImageFromChildren(nodeAny: any, elementId?: string): FxGpuSource | null {
  if (!nodeAny || typeof nodeAny.find !== "function") return null;
  try {
    const nodes = nodeAny.find("Image");
    for (const child of nodes ?? []) {
      if (elementId && typeof child?.getAttr === "function") {
        const childElementId = child.getAttr("vbElementId");
        if (childElementId && childElementId !== elementId) continue;
      }
      const image = resolveImageFromNode(child);
      if (image) return image;
    }
  } catch {}
  return null;
}

function resolveNodeSize(node: Konva.Node, source: FxGpuSource): { width: number; height: number } {
  const nodeAny = node as any;
  const width = Number(nodeAny.width?.() ?? 0);
  const height = Number(nodeAny.height?.() ?? 0);
  if (Number.isFinite(width) && width > 1 && Number.isFinite(height) && height > 1) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const fallbackWidth = (source as HTMLVideoElement).videoWidth || (source as HTMLImageElement).naturalWidth || source.width;
  const fallbackHeight = (source as HTMLVideoElement).videoHeight || (source as HTMLImageElement).naturalHeight || source.height;
  return {
    width: Math.max(1, Math.round(fallbackWidth || 1)),
    height: Math.max(1, Math.round(fallbackHeight || 1)),
  };
}

function removeFxGpuOverlay(elementId: string, overlayMap: FxGpuOverlayMap): void {
  const entry = overlayMap.get(elementId);
  if (!entry) return;
  try {
    entry.renderer.dispose();
  } catch {}
  try {
    entry.overlayNode.destroy();
  } catch {}
  overlayMap.delete(elementId);
}

function ensureOverlayEntry(args: {
  elementId: string;
  node: Konva.Node;
  parent: Konva.Container;
  overlayMap: FxGpuOverlayMap;
  moduleId: string | null;
  size: { width: number; height: number };
}): FxGpuOverlayEntry {
  const { elementId, node, parent, overlayMap, moduleId, size } = args;
  let entry = overlayMap.get(elementId);
  if (!entry || entry.sourceNode !== node || !entry.overlayNode.getParent()) {
    removeFxGpuOverlay(elementId, overlayMap);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const compositeCanvas = document.createElement("canvas");
    compositeCanvas.width = size.width;
    compositeCanvas.height = size.height;
    const overlayNode = new Konva.Image({
      id: `fx-gpu-overlay-${elementId}`,
      name: "vb-fx-gpu-overlay",
      listening: false,
      draggable: false,
      image: canvas,
      width: size.width,
      height: size.height,
    });
    parent.add(overlayNode);
    entry = {
      sourceNode: node,
      overlayNode,
      canvas,
      compositeCanvas,
      renderer: new FxGpuRenderer(canvas),
      lastRenderMs: 0,
      lastAnamorphicLogMs: 0,
      moduleId: moduleId ?? null,
      size,
    };
    overlayMap.set(elementId, entry);
  }

  if (entry.size.width !== size.width || entry.size.height !== size.height) {
    entry.size = size;
    entry.canvas.width = size.width;
    entry.canvas.height = size.height;
    entry.compositeCanvas.width = size.width;
    entry.compositeCanvas.height = size.height;
    entry.overlayNode.width(size.width);
    entry.overlayNode.height(size.height);
  }

  return entry;
}

export function renderCpuFallbackOverlay(args: {
  entry: FxGpuOverlayEntry;
  packet: FxRenderPacket;
  source: FxGpuSource;
  moduleId: string | null;
  nowMs: number;
  anamorphicDebug?: AnamorphicDebugState;
}): boolean {
  const { entry, packet, source, moduleId, nowMs, anamorphicDebug } = args;
  const width = Math.max(1, entry.size.width);
  const height = Math.max(1, entry.size.height);
  const ctx = entry.canvas.getContext("2d");
  if (!ctx) return false;

  const intensity = clamp01(toFinite(packet.params?.intensity, 1));
  const amplitude = clamp(toFinite(packet.params?.amplitude, 1), 0, 3);
  const speed = clamp(toFinite(packet.params?.speed, 1), 0.1, 4);
  const progress = clamp01(packet.progress ?? 0);
  const phase = nowMs * 0.001 * speed + packet.seed * 0.013 + progress * Math.PI * 2;
  const fallbackFamily = resolveFxCpuFallbackFamily(moduleId);
  const fallbackVariant = resolveFxCpuFallbackVariant(moduleId);
  if (import.meta.env?.DEV && packet.category === "filter" && !fallbackFamily) {
    throw new Error(`Effect not implemented: ${packet.effectType}`);
  }
  const fallbackTuning = resolveFxCpuFallbackTuning(fallbackVariant);
  const tunedIntensity = clamp01(intensity * fallbackTuning.intensityScale);
  const tunedAmplitude = clamp(amplitude * fallbackTuning.amplitudeScale, 0, 3);

  try {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (!drawSourceFrame(ctx, source, width, height)) {
      ctx.restore();
      return false;
    }

    if (fallbackFamily === "bloom-color") {
      applyBloomColorFallback(ctx, source, width, height, tunedIntensity, tunedAmplitude, phase, fallbackVariant, packet.params);
    }

    if (fallbackFamily === "color-grade") {
      applyColorGradeFallback(
        ctx,
        source,
        width,
        height,
        tunedIntensity,
        tunedAmplitude,
        fallbackVariant,
        packet.params
      );
    }

    if (fallbackFamily === "chromatic-aberration") {
      applyChromaticAberrationFallback(
        ctx,
        source,
        width,
        height,
        tunedIntensity,
        tunedAmplitude,
        fallbackVariant,
        packet.params
      );
    }

    if (fallbackFamily === "anamorphic") {
      applyAnamorphicStreakFallback(ctx, source, width, height, tunedIntensity, tunedAmplitude, phase, {
        showHighlightMask: !!anamorphicDebug?.showHighlightMask,
        validationMode: !!anamorphicDebug?.validationMode,
      }, packet.params);
    }

    if (fallbackFamily === "leak-flash") {
      applyLeakFlashFallback(ctx, width, height, tunedIntensity, phase, progress, fallbackVariant, packet.params);
    }

    if (fallbackFamily === "glitch") {
      applyGlitchFallback(
        ctx,
        source,
        width,
        height,
        tunedIntensity,
        tunedAmplitude,
        phase,
        progress,
        packet.seed,
        fallbackVariant
      );
    }

    if (fallbackFamily === "warp-blur") {
      applyWarpBlurFallback(
        ctx,
        source,
        width,
        height,
        tunedIntensity,
        tunedAmplitude,
        phase,
        progress,
        packet.seed,
        fallbackVariant
      );
    }

    if (fallbackFamily === "particle-noise") {
      applyParticleNoiseFallback(ctx, width, height, tunedIntensity, phase, packet.seed, fallbackVariant, packet.params);
    }

    if (fallbackFamily === "fog-haze") {
      applyFogHazeFallback(
        ctx,
        source,
        width,
        height,
        tunedIntensity,
        tunedAmplitude,
        phase,
        fallbackVariant,
        packet.params
      );
    }

    if (fallbackFamily === "vignette") {
      applyVignetteFallback(ctx, width, height, tunedIntensity);
    }

    ctx.restore();
    return true;
  } catch {
    try {
      ctx.restore();
    } catch {}
    return false;
  }
}

function drawSourceFrame(ctx: CanvasRenderingContext2D, source: FxGpuSource, width: number, height: number): boolean {
  try {
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    return true;
  } catch {
    return false;
  }
}

function snapshotOverlayCanvas(entry: FxGpuOverlayEntry): HTMLCanvasElement | null {
  const ctx = entry.compositeCanvas.getContext("2d");
  if (!ctx) return null;
  const width = Math.max(1, entry.canvas.width);
  const height = Math.max(1, entry.canvas.height);
  if (entry.compositeCanvas.width !== width || entry.compositeCanvas.height !== height) {
    entry.compositeCanvas.width = width;
    entry.compositeCanvas.height = height;
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(entry.canvas, 0, 0, width, height);
  return entry.compositeCanvas;
}

function applyBloomColorFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  phase: number,
  variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  const activeVariant = variant ?? "bloom-threshold";
  const debugView = Math.round(clamp(toFinite(params?.debugView, 0), 0, 2));
  const thresholdControl = clamp(toFinite(params?.threshold, 0.66), 0.3, 0.98);
  const radiusControl = clamp(toFinite(params?.radius, 0.45), 0, 1);
  const softKneeControl = clamp(toFinite(params?.softKnee, 0.34), 0, 1);
  const clampControl = clamp(toFinite(params?.clamp, 1.15), 0.4, 2);
  const knee = clamp(0.012 + softKneeControl * 0.18, 0.008, 0.25);
  const highlight = extractHighlightTintCanvas({
    source,
    width,
    height,
    threshold:
      activeVariant === "bloom-threshold"
        ? thresholdControl - intensity * (0.1 + softKneeControl * 0.12)
        : activeVariant === "halation-glow"
        ? thresholdControl - intensity * (0.05 + softKneeControl * 0.08)
        : activeVariant === "dream-glow"
        ? thresholdControl - intensity * (0.11 + softKneeControl * 0.16)
        : activeVariant === "chrome-glow"
        ? thresholdControl - intensity * (0.09 + softKneeControl * 0.12)
        : thresholdControl - intensity * (0.1 + softKneeControl * 0.1),
    knee,
    gamma:
      activeVariant === "halation-glow"
        ? 1.34
        : activeVariant === "dream-glow"
        ? 1.12
        : activeVariant === "chrome-glow"
        ? 1.3
        : 1.4,
    tint:
      activeVariant === "halation-glow"
        ? [255, 102, 62]
        : activeVariant === "dream-glow"
        ? [255, 182, 226]
        : activeVariant === "chrome-glow"
        ? [170, 218, 255]
        : activeVariant === "soft-film-fade"
        ? [252, 228, 196]
        : [220, 238, 255],
    alphaScale: activeVariant === "soft-film-fade" ? 0.62 : 1,
    downscale:
      activeVariant === "halation-glow"
        ? 0.62 + radiusControl * 0.12
        : activeVariant === "dream-glow"
        ? 0.5 + radiusControl * 0.22
        : activeVariant === "chrome-glow"
        ? 0.56 + radiusControl * 0.16
        : activeVariant === "soft-film-fade"
        ? 0.56 + radiusControl * 0.14
        : 0.5 + radiusControl * 0.18,
  });

  if (debugView === 1) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(0,0,0,1)";
    ctx.fillRect(0, 0, width, height);
    if (highlight) ctx.drawImage(highlight, 0, 0, width, height);
    ctx.restore();
    return;
  }

  if (activeVariant === "soft-film-fade") {
    const lift = clamp(toFinite(params?.lift, 0.24), 0, 1);
    const fadeSoftness = clamp(toFinite(params?.fadeSoftness, 0.52), 0, 1);
    const vignetteStrength = clamp(toFinite(params?.vignetteStrength, 0.3), 0, 1);
    const softCoreBlur = Math.max(2, Math.round(4 + intensity * 8 + fadeSoftness * 9));
    const softTailBlur = Math.max(softCoreBlur + 2, Math.round(softCoreBlur * 1.6));
    const softGlow = highlight
      ? buildBlurredGlowCanvas({
          highlight,
          width,
          height,
          coreBlur: softCoreBlur,
          tailBlur: softTailBlur,
          tailAlpha: 0.46,
          saturateCore: 1.04 + intensity * 0.18,
          saturateTail: 1.02 + intensity * 0.12,
        })
      : null;
    if (debugView === 2) {
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(0,0,0,1)";
      ctx.fillRect(0, 0, width, height);
      if (softGlow) ctx.drawImage(softGlow, 0, 0, width, height);
      ctx.restore();
      return;
    }
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.globalAlpha = 0.06 + intensity * (0.08 + lift * 0.12);
    if (softGlow) ctx.drawImage(softGlow, 0, 0, width, height);
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 0.08 + intensity * (0.1 + lift * 0.08);
    ctx.filter = `saturate(${0.86 + intensity * 0.1}) contrast(${0.86 + intensity * (0.1 + (1 - clamp(toFinite(params?.highlightRolloff, 0.44), 0, 1)) * 0.08)}) brightness(${1.01 + intensity * (0.08 + lift * 0.07) - clamp(toFinite(params?.highlightRolloff, 0.44), 0, 1) * 0.06})`;
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    ctx.filter = "none";
    const v = ctx.createRadialGradient(width * 0.5, height * 0.48, width * 0.14, width * 0.5, height * 0.5, width * 0.72);
    v.addColorStop(0, "rgba(255,240,225,0)");
    v.addColorStop(1, `rgba(18,12,10,${(0.1 + vignetteStrength * 0.2 + intensity * 0.08).toFixed(3)})`);
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    return;
  }

  const coreBlur = Math.max(
    4,
    Math.round(
      (activeVariant === "halation-glow" ? 10 : activeVariant === "dream-glow" ? 11 : activeVariant === "chrome-glow" ? 9 : 12) *
        intensity +
        (3 + radiusControl * 6) * amplitude
    )
  );
  const tailBlur = Math.max(
    coreBlur + 4,
    Math.round(
      coreBlur *
        (activeVariant === "halation-glow"
          ? 1.34
          : activeVariant === "chrome-glow"
          ? 1.32
          : activeVariant === "dream-glow"
          ? 1.78
          : 1.75)
    )
  );
  const blurCanvas = highlight
    ? buildBlurredGlowCanvas({
        highlight,
        width,
        height,
        coreBlur,
        tailBlur,
        tailAlpha: 0.56,
        saturateCore: activeVariant === "chrome-glow" ? 1.04 + intensity * 0.26 : 1.08 + intensity * 0.34,
        saturateTail: activeVariant === "chrome-glow" ? 1.02 + intensity * 0.2 : 1.03 + intensity * 0.25,
      })
    : null;
  if (debugView === 2) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(0,0,0,1)";
    ctx.fillRect(0, 0, width, height);
    if (blurCanvas) {
      ctx.drawImage(blurCanvas, 0, 0, width, height);
    } else if (highlight) {
      ctx.drawImage(highlight, 0, 0, width, height);
    }
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.globalCompositeOperation = activeVariant === "chrome-glow" ? "screen" : "lighter";
  ctx.globalAlpha =
    activeVariant === "halation-glow"
      ? 0.18 + intensity * 0.22
      : activeVariant === "chrome-glow"
      ? 0.2 + intensity * 0.24
      : 0.22 + intensity * 0.34;
  if (blurCanvas) {
    ctx.drawImage(blurCanvas, 0, 0, width, height);
  } else if (highlight) {
    ctx.drawImage(highlight, 0, 0, width, height);
  } else {
    ctx.filter = `blur(${coreBlur}px) saturate(${1.04 + intensity * 0.28})`;
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    ctx.filter = "none";
  }

  if (activeVariant === "chrome-glow") {
    const sheen = ctx.createLinearGradient(0, 0, 0, height);
    sheen.addColorStop(0, `rgba(220,238,255,${(0.025 + intensity * 0.035).toFixed(3)})`);
    sheen.addColorStop(0.5, "rgba(255,255,255,0)");
    sheen.addColorStop(1, `rgba(194,218,255,${(0.02 + intensity * 0.028).toFixed(3)})`);
    ctx.globalCompositeOperation = "overlay";
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, width, height);
  }

  if (clampControl < 1.95) {
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = clamp(1 - (clampControl - 0.4) / 1.55, 0, 1) * 0.08;
    ctx.fillStyle = "rgba(0,0,0,1)";
    ctx.fillRect(0, 0, width, height);
  }

  // subtle breathing keeps the fallback alive without adding full-frame haze.
  ctx.globalAlpha = 0.02 + intensity * 0.04;
  ctx.globalCompositeOperation = "lighter";
  ctx.drawImage(source as CanvasImageSource, Math.sin(phase * 0.42) * 0.6, 0, width, height);
  ctx.restore();
}

function buildBlurredGlowCanvas(args: {
  highlight: HTMLCanvasElement;
  width: number;
  height: number;
  coreBlur: number;
  tailBlur: number;
  tailAlpha: number;
  saturateCore: number;
  saturateTail: number;
}): HTMLCanvasElement | null {
  const { highlight, width, height, coreBlur, tailBlur, tailAlpha, saturateCore, saturateTail } = args;
  // Reuse module-level canvas — avoids allocation every frame (hot path in bloom/glow effects)
  const glow = getCpuCanvasB(width, height);
  const gctx = glow.getContext("2d");
  if (!gctx) return null;
  gctx.clearRect(0, 0, width, height);
  gctx.globalCompositeOperation = "source-over";
  gctx.globalAlpha = 1;
  gctx.filter = `blur(${coreBlur}px) saturate(${saturateCore})`;
  gctx.drawImage(highlight, 0, 0, width, height);
  gctx.globalAlpha = clamp(tailAlpha, 0, 1);
  gctx.filter = `blur(${tailBlur}px) saturate(${saturateTail})`;
  gctx.drawImage(highlight, 0, 0, width, height);
  gctx.filter = "none";
  gctx.globalAlpha = 1;
  return glow;
}

function applyColorGradeFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  if (variant === "legacy-filter-stack") {
    const blurPx = clamp(toFinite(params?.legacyBlurPx, 0), 0, 100);
    const brightness = clamp(1 + clamp(toFinite(params?.legacyBrightness, 0), -100, 100) / 100, 0, 3);
    const contrast = clamp(1 + clamp(toFinite(params?.legacyContrast, 0), -100, 100) / 100, 0, 3);
    const saturation = clamp(1 + clamp(toFinite(params?.legacySaturation, 0), -100, 100) / 100, 0, 3);
    const grayscale = clamp(toFinite(params?.legacyGrayscale, 0), 0, 100);
    const sepia = clamp(toFinite(params?.legacySepia, 0), 0, 100);
    const invert = clamp(toFinite(params?.legacyInvert, 0), 0, 100);
    const hueRotate = clamp(toFinite(params?.legacyHueRotate, 0), -720, 720);

    const cssParts: string[] = [];
    if (blurPx > 0) cssParts.push(`blur(${blurPx}px)`);
    if (Math.abs(brightness - 1) > 0.0001) cssParts.push(`brightness(${brightness})`);
    if (Math.abs(contrast - 1) > 0.0001) cssParts.push(`contrast(${contrast})`);
    if (Math.abs(saturation - 1) > 0.0001) cssParts.push(`saturate(${saturation})`);
    if (grayscale > 0) cssParts.push(`grayscale(${grayscale}%)`);
    if (sepia > 0) cssParts.push(`sepia(${sepia}%)`);
    if (invert > 0) cssParts.push(`invert(${invert}%)`);
    if (Math.abs(hueRotate) > 0.0001) cssParts.push(`hue-rotate(${hueRotate}deg)`);

    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, width, height);
    ctx.filter = cssParts.length > 0 ? cssParts.join(" ") : "none";
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    ctx.filter = "none";
    ctx.restore();
    return;
  }

  if (variant === "bleach-bypass") {
    const amount = clamp(toFinite(params?.amount, intensity), 0, 1);
    const contrast = clamp(toFinite(params?.contrast, 0.62), 0, 1);
    const saturation = clamp(toFinite(params?.saturation, 0.34), 0, 1);
    const rolloff = clamp(toFinite(params?.highlightRolloff, 0.48), 0, 1);
    const silverBias = clamp(toFinite(params?.silverBias, 0.26), 0, 1);

    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.filter = `saturate(${1 - saturation * 0.85}) contrast(${1 + contrast * 0.72}) brightness(${1 + rolloff * 0.2})`;
    ctx.globalAlpha = 0.22 + amount * 0.52;
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    ctx.filter = "none";
    ctx.globalCompositeOperation = "overlay";
    ctx.globalAlpha = 0.04 + silverBias * (0.08 + amplitude * 0.06);
    ctx.fillStyle = "rgba(214,214,214,1)";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    return;
  }

  const preset = Math.round(clamp(toFinite(params?.kodakPreset, 0), 0, 2));
  const strength = clamp(toFinite(params?.strength, intensity), 0, 1);
  const exposure = clamp(toFinite(params?.exposure, 0.08), -0.4, 0.4);
  const contrast = clamp(toFinite(params?.contrast, 0.26), 0, 1);
  const presetTone =
    preset === 1
      ? [0.96, 1.01, 1.07]
      : preset === 2
      ? [1.08, 1.03, 0.92]
      : [1.06, 1.0, 0.9];

  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.filter = `brightness(${1 + exposure * 0.7}) contrast(${1 + contrast * 0.7}) saturate(${1 + strength * 0.26})`;
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  ctx.filter = "none";
  ctx.globalCompositeOperation = "soft-light";
  ctx.globalAlpha = 0.08 + strength * 0.24;
  const grade = ctx.createLinearGradient(0, 0, width, height);
  grade.addColorStop(
    0,
    `rgba(${Math.round(255 * presetTone[0])},${Math.round(238 * presetTone[1])},${Math.round(210 * presetTone[2])},1)`
  );
  grade.addColorStop(
    1,
    `rgba(${Math.round(204 * presetTone[0])},${Math.round(220 * presetTone[1])},${Math.round(248 * presetTone[2])},1)`
  );
  ctx.fillStyle = grade;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

function applyChromaticAberrationFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  _variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  const amount = clamp(toFinite(params?.amount, intensity), 0, 1);
  const radialFalloff = clamp(toFinite(params?.radialFalloff, 0.72), 0, 1);
  const centerX = clamp(toFinite(params?.centerX, 0.5), 0, 1);
  const centerY = clamp(toFinite(params?.centerY, 0.5), 0, 1);
  const edgeStrength = clamp(toFinite(params?.edgeStrength, 0.78), 0, 1);
  const shiftPx = Math.max(0.2, amount * (0.8 + amplitude * 1.5) * (0.9 + edgeStrength * 0.9));

  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);

  const grad = ctx.createRadialGradient(
    width * centerX,
    height * centerY,
    width * 0.08,
    width * centerX,
    height * centerY,
    Math.max(width, height) * (0.5 + radialFalloff * 0.55)
  );
  grad.addColorStop(0, "rgba(255,255,255,0)");
  grad.addColorStop(1, `rgba(255,255,255,${(0.24 + edgeStrength * 0.26).toFixed(3)})`);
  ctx.globalCompositeOperation = "destination-in";
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = 0.26 + amount * 0.36;
  ctx.drawImage(source as CanvasImageSource, shiftPx, 0, width, height);
  ctx.globalAlpha = 0.22 + amount * 0.32;
  ctx.drawImage(source as CanvasImageSource, -shiftPx, 0, width, height);
  ctx.restore();
}

function applyAnamorphicStreakFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  phase: number,
  debug?: {
    showHighlightMask: boolean;
    validationMode: boolean;
  },
  params?: Record<string, number>
): void {
  const downscaleX = 0.66;
  const dw = Math.max(1, Math.round(width * downscaleX));
  const dh = Math.max(1, height);
  const scratch = document.createElement("canvas");
  scratch.width = dw;
  scratch.height = dh;
  const sctx = scratch.getContext("2d", { willReadFrequently: true });
  if (!sctx) return;

  sctx.clearRect(0, 0, dw, dh);
  sctx.drawImage(source as CanvasImageSource, 0, 0, dw, dh);

  let image: ImageData;
  try {
    image = sctx.getImageData(0, 0, dw, dh);
  } catch {
    return;
  }

  // Highlight-only extraction for thin streak lines (no global bloom).
  const cpuDiagnostics = computeAnamorphicCpuDiagnostics({
    intensity,
    amplitude,
    validationMode: !!debug?.validationMode,
    params,
  });
  const threshold = Math.round(cpuDiagnostics.threshold * 255);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    if (luma < threshold) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0;
      continue;
    }
    const k = Math.pow(clamp01((luma - threshold) / Math.max(1, 255 - threshold)), 1.5);
    data[i] = Math.min(255, Math.round(86 * k));
    data[i + 1] = Math.min(255, Math.round(176 * k));
    data[i + 2] = Math.min(255, Math.round(255 * k));
    data[i + 3] = Math.round(255 * k);
  }
  sctx.putImageData(image, 0, 0);

  if (debug?.showHighlightMask) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.drawImage(scratch, 0, 0, dw, dh, 0, 0, width, height);
    ctx.restore();
    return;
  }

  const streakReach = Math.max(8, Math.round(cpuDiagnostics.blurX));
  const dxScale = width / dw;
  const baseAlpha = (0.01 + intensity * 0.08) * cpuDiagnostics.gain;

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.imageSmoothingEnabled = true;
  for (let i = -streakReach; i <= streakReach; i += 1) {
    const fi = Math.abs(i) / Math.max(1, streakReach);
    const w = Math.exp(-fi * fi * 10);
    ctx.globalAlpha = baseAlpha * w;
    const dx = i * dxScale * 0.85;
    ctx.drawImage(scratch, 0, 0, dw, dh, dx, 0, width, height);
  }
  // Subtle long-tail pass for optical-style extension without chunky banding.
  for (let i = -12; i <= 12; i += 1) {
    const fi = Math.abs(i) / 12;
    const w = Math.exp(-fi * fi * 6);
    ctx.globalAlpha = baseAlpha * 0.28 * w;
    const dx = i * dxScale * 3.6;
    ctx.drawImage(scratch, 0, 0, dw, dh, dx, 0, width, height);
  }
  // Very small animated horizontal drift keeps streak alive without radial bloom.
  ctx.globalAlpha = 0.004 + intensity * 0.018;
  ctx.drawImage(scratch, 0, 0, dw, dh, Math.sin(phase * 0.52) * 0.6, 0, width, height);
  ctx.restore();
}

type AnamorphicDebugState = {
  log: boolean;
  showHighlightMask: boolean;
  validationMode: boolean;
  forceCpuRoute: boolean;
};

function resolveAnamorphicDebugState(args: {
  packet: FxRenderPacket;
  moduleId: string | null;
}): AnamorphicDebugState {
  const module = String(args.moduleId || "").toLowerCase();
  if (!module.includes("anamorphic")) {
    return {
      log: false,
      showHighlightMask: false,
      validationMode: false,
      forceCpuRoute: false,
    };
  }
  const params = args.packet.params ?? {};
  const log =
    readDebugFlag("vb_debug_fx_anamorphic") ||
    toFinite(params.debugAnamorphicLog, 0) >= 0.5;
  const showHighlightMask =
    readDebugFlag("vb_debug_fx_anamorphic_mask") ||
    toFinite(params.debugAnamorphicMask, 0) >= 0.5;
  const validationMode =
    readDebugFlag("vb_debug_fx_anamorphic_validate") ||
    toFinite(params.debugAnamorphicValidate, 0) >= 0.5;
  return {
    log: log || showHighlightMask || validationMode,
    showHighlightMask,
    validationMode,
    forceCpuRoute: showHighlightMask || validationMode,
  };
}

function computeAnamorphicCpuDiagnostics(args: {
  intensity: number;
  amplitude: number;
  validationMode: boolean;
  params?: Record<string, number>;
}): { threshold: number; gain: number; blurX: number; blurY: number } {
  if (args.validationMode) {
    return {
      threshold: 0.6,
      gain: 3.0,
      blurX: 48,
      blurY: 0.0,
    };
  }
  const params = args.params ?? {};
  const thresholdParam = Number.isFinite(params.threshold) ? clamp(params.threshold, 0.4, 0.99) : null;
  const clampParam = Number.isFinite(params.clamp) ? clamp(params.clamp, 0.4, 2) : null;
  const streakLengthParam = Number.isFinite(params.streakLength) ? clamp(params.streakLength, 0, 1) : null;
  const thicknessParam = Number.isFinite(params.thickness) ? clamp(params.thickness, 0, 1) : null;
  const gainParam = Number.isFinite(params.gain) ? clamp(params.gain, 0.25, 4) : null;
  return {
    threshold: thresholdParam ?? clamp((230 - 34 * args.intensity) / 255, 0, 1),
    gain: gainParam ?? (clampParam ?? 1),
    blurX:
      streakLengthParam !== null
        ? Math.max(8, 10 + streakLengthParam * 60 + args.amplitude * 6)
        : Math.max(8, 14 + args.intensity * 22 + args.amplitude * 8),
    blurY: thicknessParam !== null ? thicknessParam * 3.2 : 0.0,
  };
}

function computeAnamorphicGpuDiagnostics(args: {
  intensity: number;
  amplitude: number;
  validationMode: boolean;
}): { threshold: number; gain: number; blurX: number; blurY: number } {
  if (args.validationMode) {
    return {
      threshold: 0.6,
      gain: 3.0,
      blurX: 48,
      blurY: 0.0,
    };
  }
  const i = clamp01(args.intensity);
  return {
    threshold: 0.965 + (0.885 - 0.965) * i,
    gain: 0.04 + 0.24 * i,
    // dual-kernel effective horizontal radius estimate
    blurX: 36 + 26 * clamp01(args.amplitude * 0.5 + i * 0.5),
    blurY: 0.0,
  };
}

function maybeLogAnamorphicRoute(args: {
  entry: FxGpuOverlayEntry;
  nowMs: number;
  route: "GPU" | "CPU";
  packet: FxRenderPacket;
  moduleId: string | null;
  params: { threshold: number; gain: number; blurX: number; blurY: number };
  maskDebug: boolean;
  forcedValidation: boolean;
  chain: string;
}): void {
  const { entry, nowMs, route, packet, moduleId, params, maskDebug, forcedValidation, chain } = args;
  if (nowMs - entry.lastAnamorphicLogMs < 250) return;
  entry.lastAnamorphicLogMs = nowMs;
  try {
    console.info(
      `[FX Anamorphic] route=${route} block=${packet.fxBlockId} module=${moduleId || "none"} threshold=${params.threshold.toFixed(
        3
      )} gain=${params.gain.toFixed(3)} blurX=${params.blurX.toFixed(2)} blurY=${params.blurY.toFixed(
        2
      )} maskDebug=${maskDebug} validationMode=${forcedValidation} chain=${chain}`
    );
  } catch {}
}

function readDebugFlag(key: string): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function applyLeakFlashFallback(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  intensity: number,
  phase: number,
  progress: number,
  variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  const widthControl = clamp(toFinite(params?.width, 0.48), 0.1, 1);
  const featherControl = clamp(toFinite(params?.feather, 0.54), 0, 1);
  const tintHue = toFinite(params?.tintHue, 30);
  if (variant === "impact-flash") {
    const pulse = smoothstep(0.0, 0.24, progress) * (1 - smoothstep(0.24, 0.6, progress));
    const flicker = 0.6 + 0.4 * Math.sin(phase * 9.0);
    const alpha = (0.28 + intensity * 0.52) * pulse * flicker;
    if (alpha > 0.002) {
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = `rgba(255,255,255,${clamp01(alpha).toFixed(3)})`;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }
    return;
  }

  if (variant === "film-burn") {
    const sweep = 0.5 + 0.5 * Math.sin(phase * 0.45 + progress * Math.PI * 2);
    const position = clamp(toFinite(params?.position, 0.5), 0, 1);
    const edgeX = width * clamp(position * 0.6 + (0.12 + 0.76 * sweep) * 0.4, 0.08, 0.92);
    const span = width * (0.12 + widthControl * 0.18 + 0.12 * intensity);
    const burn = ctx.createLinearGradient(edgeX - span, 0, edgeX + span, 0);
    burn.addColorStop(0, "rgba(0,0,0,0)");
    burn.addColorStop(0.28, `rgba(255,116,36,${0.24 + intensity * (0.26 + featherControl * 0.2)})`);
    burn.addColorStop(0.5, `rgba(255,238,188,${0.32 + intensity * (0.3 + featherControl * 0.24)})`);
    burn.addColorStop(0.74, `rgba(255,132,52,${0.22 + intensity * (0.24 + featherControl * 0.18)})`);
    burn.addColorStop(1, "rgba(0,0,0,0)");

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = burn;
    ctx.fillRect(0, 0, width, height);

    const stripeCount = Math.max(4, Math.round(6 + intensity * 8));
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < stripeCount; i += 1) {
      const y = (i / stripeCount) * height;
      const jitter = Math.sin(phase * 1.6 + i * 1.2) * span * 0.2;
      ctx.fillStyle = `rgba(255,214,164,${(0.04 + intensity * 0.07).toFixed(3)})`;
      ctx.fillRect(edgeX - span * 0.55 + jitter, y, span * 1.1, Math.max(1, height / stripeCount * 0.45));
    }
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = `rgba(38,18,8,${(0.06 + intensity * 0.08).toFixed(3)})`;
    ctx.fillRect(Math.max(0, edgeX - span * 0.75), 0, Math.min(width, span * 0.22), height);
    ctx.restore();
    return;
  }

  if (variant === "beat-light-leak") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 6.6 + progress * Math.PI * 4);
    const sweep = 0.5 + 0.5 * Math.sin(phase * 0.95 + beat * 1.8);
    const centerX = sweep * width;
    const radius = Math.max(width * 0.22, width * (0.28 + 0.24 * intensity));
    const leak = ctx.createRadialGradient(centerX, height * 0.44, 0, centerX, height * 0.44, radius);
    leak.addColorStop(0, `rgba(255,234,178,${(0.18 + intensity * 0.32 * beat).toFixed(3)})`);
    leak.addColorStop(0.5, `rgba(255,122,72,${(0.08 + intensity * 0.2 * beat).toFixed(3)})`);
    leak.addColorStop(1, "rgba(0,0,0,0)");
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = leak;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    return;
  }

  if (variant === "beat-bar-flash") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 7.8 + progress * Math.PI * 3.5);
    const bars = Math.max(2, Math.round(3 + intensity * 5));
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let i = 0; i < bars; i += 1) {
      const y = ((i + 0.5) / bars) * height;
      const h = Math.max(2, Math.round(height * 0.05 * (0.8 + beat)));
      const grad = ctx.createLinearGradient(0, y - h, 0, y + h);
      grad.addColorStop(0, "rgba(255,255,255,0)");
      grad.addColorStop(0.5, `rgba(235,246,255,${(0.08 + intensity * 0.2 * beat).toFixed(3)})`);
      grad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, y - h, width, h * 2);
    }
    ctx.restore();
    return;
  }

  const sweep = 0.5 + 0.5 * Math.sin(phase * 0.8 + progress * Math.PI * 2);
  const centerX = sweep * width;
  const radius = Math.max(width * 0.14, width * (0.14 + widthControl * 0.36 + 0.12 * intensity));
  const leak = ctx.createRadialGradient(centerX, height * 0.42, 0, centerX, height * 0.42, radius);
  const warm = hueToRgb(tintHue, 0.86, 0.76);
  const hot = hueToRgb(tintHue + 18, 0.82, 0.7);
  leak.addColorStop(0, `rgba(${warm[0]},${warm[1]},${warm[2]},${0.24 + intensity * (0.32 + featherControl * 0.26)})`);
  leak.addColorStop(0.45, `rgba(${hot[0]},${hot[1]},${hot[2]},${0.14 + intensity * (0.22 + featherControl * 0.14)})`);
  leak.addColorStop(1, "rgba(0,0,0,0)");
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.fillStyle = leak;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

function applyGlitchFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  phase: number,
  progress: number,
  seed: number,
  variant: FxCpuFallbackVariant | null
): void {
  if (variant === "beat-rgb-pulse") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 8.4 + progress * Math.PI * 4.2);
    const shift = Math.max(1, Math.round((2 + amplitude * 4) * intensity * (0.4 + beat * 0.9)));
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = 0.08 + intensity * 0.16 * beat;
    ctx.drawImage(source as CanvasImageSource, -shift, 0, width, height);
    ctx.drawImage(source as CanvasImageSource, shift, 0, width, height);
    ctx.restore();
    return;
  }

  if (variant === "beat-scanline") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 7.6 + progress * Math.PI * 3.8);
    const lines = Math.max(24, Math.round(32 + intensity * 56));
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    for (let i = 0; i < lines; i += 1) {
      const y = (i / lines) * height;
      const a = (0.02 + intensity * 0.08) * (0.5 + 0.5 * Math.sin(i * 0.6 + beat * Math.PI));
      ctx.fillStyle = `rgba(0,0,0,${a.toFixed(3)})`;
      ctx.fillRect(0, y, width, 1);
    }
    ctx.restore();
    return;
  }

  if (variant === "data-mosh") {
    const stripeCount = Math.max(10, Math.round(14 + intensity * 22));
    const maxShift = Math.max(3, Math.round((6 + amplitude * 9) * intensity));
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    for (let i = 0; i < stripeCount; i += 1) {
      const y = Math.floor((i / stripeCount) * height);
      const h = Math.max(1, Math.floor(height / stripeCount));
      const hold = Math.floor((progress * 100 + i) % 5) < 3;
      const shift = hold ? Math.round((hash01(seed + i * 2.7 + phase * 0.8) - 0.5) * maxShift * 2) : 0;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(source as CanvasImageSource, 0, y, width, h, shift, y, width, h);
      ctx.imageSmoothingEnabled = true;
    }
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = 0.06 + intensity * 0.14;
    ctx.drawImage(source as CanvasImageSource, -Math.max(1, maxShift * 0.7), 0, width, height);
    ctx.drawImage(source as CanvasImageSource, Math.max(1, maxShift * 0.7), 0, width, height);
    ctx.restore();
    return;
  }

  if (variant === "glitch-tear") {
    const bandCount = Math.max(8, Math.round(10 + intensity * 18));
    const maxShift = Math.max(3, Math.round((4 + amplitude * 7) * intensity));
    ctx.save();
    for (let i = 0; i < bandCount; i += 1) {
      const y = Math.floor((i / bandCount) * height);
      const h = Math.max(1, Math.floor(height / bandCount));
      const gate = hash01(seed + i * 5.31 + Math.floor(phase * 9.0)) > 0.63;
      const shift = gate ? Math.round((hash01(seed + i * 7.7 + phase) - 0.5) * maxShift * 2) : 0;
      ctx.drawImage(source as CanvasImageSource, 0, y, width, h, shift, y, width, h);
    }
    ctx.restore();
    return;
  }

  if (variant === "screen-crack") {
    const rayCount = Math.max(7, Math.round(10 + intensity * 16));
    const centerX = width * 0.5;
    const centerY = height * (0.45 + 0.04 * Math.sin(phase * 0.7));
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let i = 0; i < rayCount; i += 1) {
      const a = ((Math.PI * 2) / rayCount) * i + hash01(seed + i * 3.11) * 0.35;
      const len = Math.max(width, height) * (0.45 + hash01(seed + i * 9.71) * 0.5);
      const x2 = centerX + Math.cos(a) * len;
      const y2 = centerY + Math.sin(a) * len;
      ctx.strokeStyle = `rgba(245,248,255,${(0.05 + intensity * 0.15).toFixed(3)})`;
      ctx.lineWidth = 1 + Math.floor(hash01(seed + i * 1.23) * 2);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  if (variant === "chromatic-glitch") {
    const shift = Math.max(1, Math.round((2 + amplitude * 4) * intensity));
    const sliceCount = Math.max(6, Math.round(8 + intensity * 10));
    ctx.save();
    for (let i = 0; i < sliceCount; i += 1) {
      const y = Math.floor((i / sliceCount) * height);
      const h = Math.max(1, Math.floor(height / sliceCount));
      const xJit = Math.round(Math.sin(phase * 2.1 + i * 1.9) * shift);
      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(source as CanvasImageSource, 0, y, width, h, xJit, y, width, h);
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = 0.06 + intensity * 0.12;
      ctx.drawImage(source as CanvasImageSource, xJit - shift, y, width, h);
      ctx.drawImage(source as CanvasImageSource, xJit + shift, y, width, h);
    }
    ctx.restore();
    return;
  }

  const sliceCount = Math.max(6, Math.round(10 + intensity * 12));
  const maxShift = Math.max(2, Math.round((4 + amplitude * 8) * intensity));
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  for (let i = 0; i < sliceCount; i += 1) {
    const y = Math.floor((i / sliceCount) * height);
    const h = Math.max(1, Math.floor(height / sliceCount));
    const shift = Math.round(Math.sin(phase * 2.2 + i * 1.7) * maxShift);
    ctx.drawImage(source as CanvasImageSource, 0, y, width, h, shift, y, width, h);
  }
  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = 0.08 + intensity * 0.16;
  ctx.drawImage(source as CanvasImageSource, -maxShift, 0, width, height);
  ctx.drawImage(source as CanvasImageSource, maxShift, 0, width, height);
  ctx.restore();
}

function applyWarpBlurFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  phase: number,
  progress: number,
  seed: number,
  variant: FxCpuFallbackVariant | null
): void {
  if (variant === "impact-shockwave") {
    const centerX = width * 0.5;
    const centerY = height * 0.5;
    const radius = Math.max(8, Math.round((0.08 + progress * 0.7) * Math.min(width, height)));
    const thickness = Math.max(6, Math.round(10 + intensity * 18));
    const expand = 1 + 0.01 + intensity * 0.05;
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + thickness, 0, Math.PI * 2);
    ctx.arc(centerX, centerY, Math.max(1, radius - thickness), 0, Math.PI * 2, true);
    ctx.clip();
    ctx.globalAlpha = 0.55;
    ctx.drawImage(
      source as CanvasImageSource,
      centerX - (width * 0.5) * expand,
      centerY - (height * 0.5) * expand,
      width * expand,
      height * expand
    );
    ctx.restore();
    return;
  }

  if (variant === "tempo-ripple") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 7.0 + progress * Math.PI * 3.5);
    const ringCount = 3;
    ctx.save();
    for (let i = 0; i < ringCount; i += 1) {
      const local = (progress + i / ringCount) % 1;
      const ring = local * Math.min(width, height) * 0.58;
      const t = 1 - local;
      const shift = (0.5 + beat * 0.9) * intensity * (2 + amplitude * 2) * t;
      ctx.save();
      ctx.beginPath();
      ctx.arc(width * 0.5, height * 0.5, ring + 12, 0, Math.PI * 2);
      ctx.arc(width * 0.5, height * 0.5, Math.max(1, ring - 12), 0, Math.PI * 2, true);
      ctx.clip();
      ctx.globalAlpha = 0.16 + 0.2 * t;
      ctx.drawImage(source as CanvasImageSource, shift, 0, width, height);
      ctx.restore();
    }
    ctx.restore();
    return;
  }

  if (variant === "speed-ramp-blur") {
    const accel = smoothstep(0.0, 0.22, progress) * (1 - smoothstep(0.68, 1.0, progress));
    const blurPx = Math.max(1, Math.round(5 + intensity * 10 + accel * 8));
    const shift = (3 + amplitude * 7) * (0.35 + accel);
    const dirX = 1;
    const dirY = 0.24;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 0.14 + intensity * 0.16;
    ctx.filter = `blur(${blurPx}px)`;
    for (let i = 0; i < 5; i += 1) {
      const t = (i - 2) / 2;
      const dx = t * dirX * shift;
      const dy = t * dirY * shift;
      ctx.drawImage(source as CanvasImageSource, dx, dy, width, height);
    }
    ctx.filter = "none";
    ctx.restore();
    return;
  }

  if (variant === "heat-distortion") {
    const lineCount = Math.max(42, Math.round(58 + intensity * 24));
    const wave = (1.2 + intensity * 2.4) * Math.max(0.2, amplitude);
    ctx.save();
    for (let i = 0; i < lineCount; i += 1) {
      const y = Math.floor((i / lineCount) * height);
      const h = Math.max(1, Math.floor(height / lineCount) + 1);
      const dx = Math.sin((y / Math.max(1, height)) * 40 + phase * 5.0 + seed * 0.01) * wave;
      ctx.drawImage(source as CanvasImageSource, 0, y, width, h, dx, y, width, h);
    }
    ctx.restore();
    return;
  }

  if (variant === "water-droplets") {
    const count = Math.max(10, Math.round(14 + intensity * 20));
    const rMin = Math.max(4, Math.round(width * 0.008));
    const rMax = Math.max(rMin + 2, Math.round(width * 0.028));
    ctx.save();
    for (let i = 0; i < count; i += 1) {
      const x = hash01(seed + i * 5.23 + phase * 0.12) * width;
      const y = hash01(seed + i * 7.19 + phase * 0.1) * height;
      const r = rMin + hash01(seed + i * 2.71) * (rMax - rMin);
      const magnify = 1.02 + intensity * 0.06;
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalAlpha = 0.26 + intensity * 0.22;
      ctx.drawImage(
        source as CanvasImageSource,
        x - r * magnify,
        y - r * magnify,
        r * 2 * magnify,
        r * 2 * magnify,
        x - r,
        y - r,
        r * 2,
        r * 2
      );
      ctx.restore();
      ctx.strokeStyle = `rgba(210,228,245,${(0.08 + intensity * 0.16).toFixed(3)})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  const trailCount = Math.max(2, Math.round(3 + intensity * 4));
  const blurPx = Math.max(1, Math.round(3 + intensity * 8));
  const shiftBase = (2 + amplitude * 5) * intensity;
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 0.12 + intensity * 0.14;
  ctx.filter = `blur(${blurPx}px)`;
  for (let i = 0; i < trailCount; i += 1) {
    const t = i / Math.max(1, trailCount - 1);
    const dx = Math.sin(phase * 1.8 + t * Math.PI * 2) * shiftBase;
    const dy = Math.cos(phase * 1.2 + t * Math.PI * 2) * (shiftBase * 0.6);
    ctx.drawImage(source as CanvasImageSource, dx, dy, width, height);
  }
  ctx.filter = "none";
  ctx.restore();
}

function applyParticleNoiseFallback(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  intensity: number,
  phase: number,
  seed: number,
  variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  if (variant === "beat-particle-burst") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 8.8);
    const count = Math.max(26, Math.round(48 + intensity * 82));
    const cx = width * 0.5;
    const cy = height * 0.5;
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let i = 0; i < count; i += 1) {
      const a = hash01(seed + i * 3.13) * Math.PI * 2;
      const d = hash01(seed + i * 5.21 + phase * 0.25) * Math.min(width, height) * (0.06 + 0.34 * beat);
      const x = cx + Math.cos(a) * d;
      const y = cy + Math.sin(a) * d;
      const r = 0.8 + hash01(seed + i * 1.57) * (1.4 + intensity * 1.6);
      const alpha = 0.07 + 0.22 * beat * hash01(seed + i * 9.91);
      ctx.fillStyle = `rgba(245,236,196,${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    return;
  }

  if (variant === "treble-sparkle") {
    const pulse = 0.5 + 0.5 * Math.sin(phase * 12.5);
    const count = Math.max(20, Math.round(36 + intensity * 60));
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let i = 0; i < count; i += 1) {
      const x = hash01(seed + i * 7.11 + phase * 0.33) * width;
      const y = hash01(seed + i * 4.67 + phase * 0.29) * height;
      const r = 0.5 + hash01(seed + i * 1.93) * (0.9 + intensity * 1.1);
      const alpha = 0.06 + hash01(seed + i * 8.47) * (0.14 + pulse * 0.2);
      ctx.fillStyle = `rgba(210,230,255,${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    return;
  }

  if (variant === "film-grain") {
    const grainSize = clamp(toFinite(params?.grainSize, 0.52), 0.1, 1);
    const softness = clamp(toFinite(params?.softness, 0.34), 0, 1);
    const lumaBias = clamp(toFinite(params?.lumaBias, 0.52), 0, 1);
    const monochrome = toFinite(params?.monochrome, 1) > 0.5;
    const grainW = Math.max(96, Math.round(width * (0.08 + grainSize * 0.2)));
    const grainH = Math.max(96, Math.round(height * (0.08 + grainSize * 0.2)));
    const grain = document.createElement("canvas");
    grain.width = grainW;
    grain.height = grainH;
    const gctx = grain.getContext("2d", { willReadFrequently: true });
    if (gctx) {
      let img: ImageData;
      try {
        img = gctx.createImageData(grainW, grainH);
      } catch {
        img = null as unknown as ImageData;
      }
      if (img) {
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const n = Math.round(88 + hash01(seed + i * 0.013 + phase * (1.2 + softness * 2.2)) * 120);
          const chroma = Math.round((hash01(seed + i * 0.021 + phase * 1.6) - 0.5) * 26 * (1 - (monochrome ? 1 : 0)));
          const lumMix = Math.round((1 - lumaBias) * 12);
          d[i] = clamp(Math.round(n + chroma * 0.3 + lumMix * 0.2), 0, 255);
          d[i + 1] = clamp(Math.round(n + chroma * 0.08 + lumMix * 0.1), 0, 255);
          d[i + 2] = clamp(Math.round(n - chroma * 0.32 - lumMix * 0.1), 0, 255);
          d[i + 3] = Math.round(16 + intensity * (22 + softness * 18));
        }
        gctx.putImageData(img, 0, 0);
      }
      ctx.save();
      ctx.globalCompositeOperation = "soft-light";
      ctx.globalAlpha = 0.2 + intensity * (0.16 + (1 - softness) * 0.22);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(grain, 0, 0, width, height);
      ctx.globalCompositeOperation = "multiply";
      ctx.globalAlpha = 0.06 + intensity * (0.06 + (1 - lumaBias) * 0.08);
      ctx.fillStyle = "rgba(72,54,38,1)";
      ctx.fillRect(0, 0, width, height);
      ctx.imageSmoothingEnabled = true;
      ctx.restore();
    }
    return;
  }

  if (variant === "film-scratches") {
    const count = Math.max(6, Math.round(8 + intensity * 10));
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let i = 0; i < count; i += 1) {
      const x = hash01(seed + i * 1.77 + phase * 0.12) * width;
      const alpha = 0.05 + hash01(seed + i * 2.11) * (0.09 + intensity * 0.18);
      const w = 1 + Math.floor(hash01(seed + i * 9.7) * 2);
      ctx.fillStyle = `rgba(245,245,235,${alpha.toFixed(3)})`;
      ctx.fillRect(x, 0, w, height);
    }
    ctx.restore();
    return;
  }

  if (variant === "lens-dust") {
    const density = clamp(toFinite(params?.density, 0.28), 0, 1);
    const sizeControl = clamp(toFinite(params?.size, 0.42), 0.1, 1);
    const opacity = clamp(toFinite(params?.opacity, 0.32), 0, 1);
    const drift = clamp(toFinite(params?.driftSpeed, 0.58), 0, 1);
    const blur = clamp(toFinite(params?.blur, 0.28), 0, 1);
    const count = Math.max(12, Math.round(18 + density * 110));
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.filter = `blur(${Math.max(0, Math.round(blur * 3))}px)`;
    for (let i = 0; i < count; i += 1) {
      const x = hash01(seed + i * 7.91 + phase * (0.6 + drift * 1.4)) * width;
      const y = hash01(seed + i * 4.23 + phase * (0.5 + drift * 1.2)) * height;
      const r = 0.5 + hash01(seed + i * 2.13) * (0.8 + sizeControl * 3.4);
      const alpha = (0.02 + hash01(seed + i * 9.37 + phase) * (0.08 + intensity * 0.12)) * (0.5 + opacity * 1.1);
      const c = 230 + Math.round(hash01(seed + i * 3.31) * 20);
      ctx.fillStyle = `rgba(${c},${c - 6},${c - 14},${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.filter = "none";
    ctx.restore();
    return;
  }

  const count = Math.max(24, Math.round(90 * (0.3 + intensity)));
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  for (let i = 0; i < count; i += 1) {
    const x = hash01(seed + i * 7.91 + phase * 2.1) * width;
    const y = hash01(seed + i * 4.23 + phase * 1.7) * height;
    const r = 0.4 + hash01(seed + i * 2.13) * (1.1 + intensity * 1.9);
    const alpha = 0.08 + hash01(seed + i * 9.37 + phase) * (0.24 + intensity * 0.22);
    ctx.fillStyle = `rgba(255,240,220,${alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function extractHighlightTintCanvas(args: {
  source: FxGpuSource;
  width: number;
  height: number;
  threshold: number;
  knee: number;
  gamma: number;
  tint: [number, number, number];
  alphaScale: number;
  downscale: number;
}): HTMLCanvasElement | null {
  const { source, width, height, tint, gamma, alphaScale } = args;
  const downscale = clamp(args.downscale, 0.2, 1);
  const dw = Math.max(1, Math.round(width * downscale));
  const dh = Math.max(1, Math.round(height * downscale));
  // Reuse module-level canvas — avoids allocation every frame (hot path in bloom/glow effects)
  const canvas = getCpuCanvasA(dw, dh);
  const c = canvas.getContext("2d", { willReadFrequently: true });
  if (!c) return null;
  try {
    c.drawImage(source as CanvasImageSource, 0, 0, dw, dh);
    const img = c.getImageData(0, 0, dw, dh);
    const d = img.data;
    const threshold = clamp(args.threshold, 0, 1);
    const knee = clamp(args.knee, 0.0001, 1);
    const tintLinear = [srgb8ToLinear(tint[0]), srgb8ToLinear(tint[1]), srgb8ToLinear(tint[2])] as const;
    for (let i = 0; i < d.length; i += 4) {
      const rLin = srgb8ToLinear(d[i]);
      const gLin = srgb8ToLinear(d[i + 1]);
      const bLin = srgb8ToLinear(d[i + 2]);
      const luma = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
      const mask = smoothstep(threshold - knee, threshold + knee, luma);
      if (mask <= 0.0001) {
        d[i + 3] = 0;
        continue;
      }
      const k = Math.pow(clamp01(mask), gamma);
      d[i] = linearToSrgb8(tintLinear[0] * k);
      d[i + 1] = linearToSrgb8(tintLinear[1] * k);
      d[i + 2] = linearToSrgb8(tintLinear[2] * k);
      d[i + 3] = Math.round(255 * k * clamp01(alphaScale));
    }
    c.putImageData(img, 0, 0);
    return canvas;
  } catch {
    return null;
  }
}

function applyFogHazeFallback(
  ctx: CanvasRenderingContext2D,
  source: FxGpuSource,
  width: number,
  height: number,
  intensity: number,
  amplitude: number,
  phase: number,
  variant: FxCpuFallbackVariant | null,
  params?: Record<string, number>
): void {
  const density = clamp(toFinite(params?.hazeDensity, amplitude / 3), 0, 1);
  const tintWarmth = clamp((toFinite(params?.hazeTint, 0.08) + 1) * 0.5, 0, 1);
  const dw = Math.max(1, Math.round(width * 0.58));
  const dh = Math.max(1, Math.round(height * 0.58));
  // Reuse module-level canvas — avoids allocation every frame. Fog/haze and bloom are
  // mutually exclusive fallback families so sharing _cpuCanvasA is safe.
  const hazeCanvas = getCpuCanvasA(dw, dh);
  const hctx = hazeCanvas.getContext("2d", { willReadFrequently: true });
  if (!hctx) return;
  try {
    hctx.drawImage(source as CanvasImageSource, 0, 0, dw, dh);
    const img = hctx.getImageData(0, 0, dw, dh);
    const d = img.data;
    const coolTint = [0.68, 0.78, 0.9] as const;
    const warmTint = [0.93, 0.84, 0.72] as const;
    const tint = [coolTint[0] * (1 - tintWarmth) + warmTint[0] * tintWarmth, coolTint[1] * (1 - tintWarmth) + warmTint[1] * tintWarmth, coolTint[2] * (1 - tintWarmth) + warmTint[2] * tintWarmth] as const;
    const depthBoost = variant === "fog-depth" ? 1.2 : 1;
    for (let y = 0; y < dh; y += 1) {
      const v = dh > 1 ? y / (dh - 1) : 0;
      const depth = Math.pow(v, 1.55 - density * 0.58) * depthBoost;
      for (let x = 0; x < dw; x += 1) {
        const idx = (y * dw + x) * 4;
        const u = dw > 1 ? x / (dw - 1) : 0;
        const rLin = srgb8ToLinear(d[idx]);
        const gLin = srgb8ToLinear(d[idx + 1]);
        const bLin = srgb8ToLinear(d[idx + 2]);
        const luma = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
        const driftA = 0.5 + 0.5 * Math.sin(v * 9.2 + phase * (0.16 + density * 0.26) + u * 3.8);
        const driftB = 0.5 + 0.5 * Math.sin(u * 6.4 - phase * (0.11 + density * 0.2));
        const field = clamp(depth * 0.52 + driftA * 0.29 + driftB * 0.19, 0, 1);
        const shadowProtect = smoothstep(0.1, 0.34, luma);
        const midWeight = smoothstep(0.18, 0.72, luma) * (1 - smoothstep(0.72, 0.96, luma));
        const highlight = smoothstep(0.34, 0.94, luma);
        const hazeMask = field * (0.03 + density * 0.2) * intensity * (0.16 + midWeight * 0.38 + highlight * 0.56) * (0.34 + shadowProtect * 0.66);
        const lift = (0.004 + density * 0.014) * (0.2 + depth * 0.8) * (0.2 + highlight * 0.8);
        const baseR = rLin + lift;
        const baseG = gLin + lift;
        const baseB = bLin + lift;
        const screenR = 1 - (1 - baseR) * (1 - tint[0] * hazeMask);
        const screenG = 1 - (1 - baseG) * (1 - tint[1] * hazeMask);
        const screenB = 1 - (1 - baseB) * (1 - tint[2] * hazeMask);
        const blend = clamp(0.48 + density * 0.24, 0, 1);
        d[idx] = linearToSrgb8(rLin * (1 - blend) + screenR * blend);
        d[idx + 1] = linearToSrgb8(gLin * (1 - blend) + screenG * blend);
        d[idx + 2] = linearToSrgb8(bLin * (1 - blend) + screenB * blend);
      }
    }
    hctx.putImageData(img, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(hazeCanvas, 0, 0, width, height);
    ctx.restore();
  } catch {
    const cx = width * (0.5 + 0.22 * Math.sin(phase * 0.35));
    const cy = height * (0.48 + 0.12 * Math.cos(phase * 0.27));
    const fog = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height) * 0.8);
    fog.addColorStop(0, `rgba(230,236,246,${0.08 + intensity * 0.2})`);
    fog.addColorStop(1, "rgba(230,236,246,0)");
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = fog;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}

function applyVignetteFallback(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  intensity: number
): void {
  const radius = Math.max(width, height) * 0.68;
  const gradient = ctx.createRadialGradient(
    width * 0.5,
    height * 0.5,
    radius * 0.45,
    width * 0.5,
    height * 0.5,
    radius
  );
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, `rgba(0,0,0,${0.28 * clamp01(intensity)})`);
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

function srgb8ToLinear(value: number): number {
  const srgb = clamp(value / 255, 0, 1);
  return Math.pow(srgb, 2.2);
}

function linearToSrgb8(value: number): number {
  const srgb = Math.pow(clamp(value, 0, 1), 1 / 2.2);
  return Math.round(srgb * 255);
}

function toFinite(value: unknown, fallback: number): number {
  return Number.isFinite(value) ? Number(value) : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function hash01(value: number): number {
  const s = Math.sin(value * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

function hueToRgb(hueDeg: number, sat: number, light: number): [number, number, number] {
  const h = ((hueDeg % 360) + 360) % 360 / 360;
  const s = clamp(sat, 0, 1);
  const l = clamp(light, 0, 1);
  if (s <= 0) {
    const gray = Math.round(l * 255);
    return [gray, gray, gray];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (tIn: number): number => {
    let t = tIn;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(hue2rgb(h + 1 / 3) * 255),
    Math.round(hue2rgb(h) * 255),
    Math.round(hue2rgb(h - 1 / 3) * 255),
  ];
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / Math.max(1e-6, edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function markGpuModuleFailure(moduleId: string, nowMs: number): void {
  gpuModuleFailureUntilMs.set(moduleId, nowMs + GPU_MODULE_RETRY_DELAY_MS);
  if (gpuModuleFailureLogged.has(moduleId)) return;
  gpuModuleFailureLogged.add(moduleId);
  try {
    // One-shot debug log per module; fallback stays active on CPU path.
    console.debug(
      `[FX GPU] Module "${moduleId}" failed; using CPU fallback profile for ${Math.round(
        GPU_MODULE_RETRY_DELAY_MS / 1000
      )}s.`
    );
  } catch {}
}

function syncOverlayTransform(overlay: Konva.Image, source: Konva.Node): void {
  const overlayAny = overlay as any;
  const sourceAny = source as any;
  overlay.x(source.x());
  overlay.y(source.y());
  overlay.scaleX(source.scaleX());
  overlay.scaleY(source.scaleY());
  overlay.rotation(source.rotation());
  overlay.offsetX(source.offsetX());
  overlay.offsetY(source.offsetY());
  overlay.visible(source.visible());
  overlay.listening(false);
  if (typeof overlayAny.skewX === "function" && typeof sourceAny.skewX === "function") {
    overlayAny.skewX(sourceAny.skewX());
  }
  if (typeof overlayAny.skewY === "function" && typeof sourceAny.skewY === "function") {
    overlayAny.skewY(sourceAny.skewY());
  }
}

function placeOverlayAbove(args: { parent: Konva.Container; source: Konva.Node; overlay: Konva.Node }): void {
  const { parent, source, overlay } = args;
  const nextIndex = Math.min(parent.getChildren().length - 1, source.getZIndex() + 1);
  if (overlay.getZIndex() !== nextIndex) {
    overlay.zIndex(nextIndex);
  }
}
